import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, Filter, Loader2, ArrowUpDown, ArrowUp, ArrowDown, FileDown, RefreshCw,
  Download, AlertTriangle, BookUser, Pencil, Copy, GitMerge
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import PaymentContactEditModal from './payment-contacts/PaymentContactEditModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  banca_di_asti: { label: 'Banca di Asti', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  xml_sabadell: { label: 'XML Sabadell', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  xml_booking: { label: 'XML Booking', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  xml_fatture: { label: 'XML Fatture', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  xml_other: { label: 'XML Altro', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  fattura_inline: { label: 'Fattura inline', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
};
const CATEGORY_LABELS = {
  da_classificare: { label: 'Da classificare', color: 'bg-amber-100 text-amber-800' },
  proprietario: { label: 'Proprietario', color: 'bg-blue-100 text-blue-800' },
  fornitore: { label: 'Fornitore', color: 'bg-emerald-100 text-emerald-800' },
  partner: { label: 'Partner', color: 'bg-purple-100 text-purple-800' },
  comune: { label: 'Comune', color: 'bg-cyan-100 text-cyan-800' },
  altro: { label: 'Altro', color: 'bg-slate-100 text-slate-700' },
};
const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('it-IT'); } catch (_) { return s.slice(0, 10); }
};

export default function PaymentContactsPage() {
  const { user } = useAuth();
  const editorRoles = new Set(['admin', 'invoice_operator', 'bookings_operator', 'customer_service']);
  const canEditContacts = editorRoles.has(user?.role);
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState({});
  const [sources, setSources] = useState({});

  // Filtri
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');
  const [minCount, setMinCount] = useState('');
  const [seenAfter, setSeenAfter] = useState('');
  const [seenBefore, setSeenBefore] = useState('');

  // Sort
  const [sortBy, setSortBy] = useState('total_invoiced');
  const [sortDir, setSortDir] = useState('desc');

  // Backfill state
  const [extracting, setExtracting] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Duplicati IBAN
  const [showDups, setShowDups] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupScanning, setDupScanning] = useState(false);
  const [dupKeep, setDupKeep] = useState({});
  const [merging, setMerging] = useState('');

  const token = localStorage.getItem('hw_token');

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.set('category', filterCategory);
      if (filterSource !== 'all') params.set('source', filterSource);
      if (search) params.set('search', search);
      if (minTotal) params.set('min_total', minTotal);
      if (maxTotal) params.set('max_total', maxTotal);
      if (minCount) params.set('min_count', minCount);
      if (seenAfter) params.set('seen_after', seenAfter);
      if (seenBefore) params.set('seen_before', seenBefore);
      params.set('sort_by', sortBy);
      params.set('sort_dir', sortDir);

      const r = await fetch(`${API_URL}/api/payment-contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setContacts(data.contacts || []);
      setCategories(data.categories || {});
      setSources(data.sources || {});
    } catch (e) {
      toast.error(`Errore caricamento: ${e.message}`);
    }
    setLoading(false);
  }, [token, search, filterCategory, filterSource, minTotal, maxTotal, minCount, seenAfter, seenBefore, sortBy, sortDir]);

  useEffect(() => {
    const t = setTimeout(fetchContacts, 300);
    return () => clearTimeout(t);
  }, [fetchContacts]);

  const handleExtractFromHistory = async () => {
    if (!window.confirm("Estrarre i beneficiari da tutti gli XML salvati storicamente? Aggiorna conteggi e aliases dei contatti esistenti.")) return;
    setExtracting(true);
    try {
      const r = await fetch(`${API_URL}/api/payment-contacts/extract-from-history`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(`Estratti ${data.new} nuovi + ${data.updated} aggiornati da ${data.batches_scanned} batch`);
      fetchContacts();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
    setExtracting(false);
  };

  const scanDuplicates = async () => {
    setDupScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/payment-contacts/iban-duplicates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const groups = data.groups || [];
      setDupGroups(groups);
      setDupKeep(Object.fromEntries(groups.map((g) => [g.iban, g.suggested_keep_id])));
      setShowDups(true);
      if (!groups.length) toast.success('Nessun duplicato: ogni IBAN ha un solo contatto ✓');
    } catch (e) {
      toast.error('Errore scansione duplicati');
    } finally {
      setDupScanning(false);
    }
  };

  const mergeGroup = async (group) => {
    const keepId = dupKeep[group.iban];
    const mergeIds = group.contacts.filter((c) => c.id !== keepId).map((c) => c.id);
    if (!keepId || !mergeIds.length) return;
    setMerging(group.iban);
    try {
      const res = await fetch(`${API_URL}/api/payment-contacts/merge-duplicates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep_id: keepId, merge_ids: mergeIds }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Uniti in "${data.contact?.name}" (${data.merged.length} contatti assorbiti come alias)`);
      setDupGroups((prev) => prev.filter((g) => g.iban !== group.iban));
      fetchContacts();
    } catch (e) {
      toast.error('Errore durante l\'unione');
    } finally {
      setMerging('');
    }
  };

  const exportCSV = () => {
    const headers = ['Nome', 'IBAN', 'Categoria', 'Origini', 'Aliases', 'N° pagamenti', 'Totale storico €', 'Ultimo pagamento', 'Ultimo importo €'];
    const rows = contacts.map(c => [
      c.name || '', c.iban || '', c.category || '',
      (c.sources || [c.source]).filter(Boolean).join('; '),
      (c.aliases || []).join('; '),
      c.transactions_count || 0,
      (c.total_invoiced || 0).toFixed(2),
      (c.last_seen_date || '').slice(0, 10),
      (c.last_amount || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rubrica_beneficiari_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${contacts.length} contatti in CSV`);
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'name' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const resetFilters = () => {
    setSearch(''); setFilterCategory('all'); setFilterSource('all');
    setMinTotal(''); setMaxTotal(''); setMinCount(''); setSeenAfter(''); setSeenBefore('');
  };

  return (
    <div className="space-y-4" data-testid="payment-contacts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookUser className="h-7 w-7 text-blue-600" />
            Rubrica Beneficiari
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {contacts.length} contatti · totale storico{' '}
            <strong>{fmtEur(contacts.reduce((s, c) => s + (c.total_invoiced || 0), 0))}</strong> ·{' '}
            <strong>{contacts.reduce((s, c) => s + (c.transactions_count || 0), 0)}</strong> pagamenti
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
            <FileDown className="h-4 w-4 mr-2" /> Esporta CSV
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={scanDuplicates}
              disabled={dupScanning}
              data-testid="scan-iban-duplicates-btn"
            >
              {dupScanning
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scansione...</>
                : <><Copy className="h-4 w-4 mr-2" /> Duplicati IBAN</>}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExtractFromHistory}
              disabled={extracting}
              data-testid="extract-history-btn"
            >
              {extracting
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Estrazione...</>
                : <><RefreshCw className="h-4 w-4 mr-2" /> Estrai da XML storici</>}
            </Button>
          )}
        </div>
      </div>

      {/* Filtri */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Filtri</span>
          <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={resetFilters}>
            Resetta filtri
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Ricerca</label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome / IBAN / alias..."
                className="pl-8 h-9"
                data-testid="search-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Categoria</label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-9" data-testid="category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte ({Object.values(categories).reduce((a, b) => a + b, 0)})</SelectItem>
                {Object.entries(categories).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{(CATEGORY_LABELS[k] || { label: k }).label} ({v})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Origine</label>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="h-9" data-testid="source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {Object.entries(sources).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{(SOURCE_LABELS[k] || { label: k }).label} ({v})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600">Min pagamenti</label>
              <Input type="number" min="0" value={minCount} onChange={e => setMinCount(e.target.value)} className="h-9" placeholder="es. 3" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Min totale €</label>
            <Input type="number" min="0" step="100" value={minTotal} onChange={e => setMinTotal(e.target.value)} className="h-9" placeholder="es. 1000" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Max totale €</label>
            <Input type="number" min="0" step="100" value={maxTotal} onChange={e => setMaxTotal(e.target.value)} className="h-9" placeholder="es. 50000" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Ultimo pagamento dal</label>
            <Input type="date" value={seenAfter} onChange={e => setSeenAfter(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Ultimo pagamento al</label>
            <Input type="date" value={seenBefore} onChange={e => setSeenBefore(e.target.value)} className="h-9" />
          </div>
        </div>
      </Card>

      {/* Tabella */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700 sticky top-0 border-b">
              <tr>
                <th className="text-left px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('name')}>
                  <div className="flex items-center gap-1 font-semibold">Nome <SortIcon col="name" /></div>
                </th>
                <th className="text-left px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('iban')}>
                  <div className="flex items-center gap-1 font-semibold">IBAN <SortIcon col="iban" /></div>
                </th>
                <th className="text-left px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('category')}>
                  <div className="flex items-center gap-1 font-semibold">Categoria <SortIcon col="category" /></div>
                </th>
                <th className="text-left px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('source')}>
                  <div className="flex items-center gap-1 font-semibold">Origine <SortIcon col="source" /></div>
                </th>
                <th className="text-right px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('transactions_count')}>
                  <div className="flex items-center gap-1 font-semibold justify-end">N° pag. <SortIcon col="transactions_count" /></div>
                </th>
                <th className="text-right px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('total_invoiced')}>
                  <div className="flex items-center gap-1 font-semibold justify-end">Totale storico <SortIcon col="total_invoiced" /></div>
                </th>
                <th className="text-right px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('last_amount')}>
                  <div className="flex items-center gap-1 font-semibold justify-end">Ultimo importo <SortIcon col="last_amount" /></div>
                </th>
                <th className="text-left px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('last_seen_date')}>
                  <div className="flex items-center gap-1 font-semibold">Ultimo pagamento <SortIcon col="last_seen_date" /></div>
                </th>
                <th className="text-right px-3 py-2 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr><td colSpan={9} className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
              )}
              {!loading && contacts.length === 0 && (
                <tr><td colSpan={9} className="py-12 text-center text-slate-400">
                  <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                  Nessun contatto trovato con i filtri correnti
                </td></tr>
              )}
              {!loading && contacts.map((c) => {
                const srcArr = (c.sources && c.sources.length ? c.sources : (c.source ? [c.source] : []));
                const cat = CATEGORY_LABELS[c.category] || { label: c.category || '—', color: 'bg-slate-100 text-slate-700' };
                const canEdit = canEditContacts;
                return (
                  <tr
                    key={c.id}
                    className={`${canEdit ? 'cursor-pointer' : ''} hover:bg-blue-50/40 transition`}
                    onClick={() => canEdit && setEditingContact(c)}
                    data-testid={`contact-row-${c.id}`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {c.name}
                        {canEdit && <Pencil className="h-3 w-3 text-slate-300 group-hover:text-slate-600" />}
                      </div>
                      {(c.aliases || []).length > 0 && (
                        <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[260px]" title={(c.aliases || []).join(' · ')}>
                          alias: {(c.aliases || []).slice(0, 2).join(' · ')}{(c.aliases || []).length > 2 ? ' ...' : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700">
                      {c.iban ? `${c.iban.slice(0, 10)}…${c.iban.slice(-4)}` : <span className="text-red-500">— manca</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={cat.color + ' text-[10px]'}>{cat.label}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {srcArr.map(s => {
                          const meta = SOURCE_LABELS[s] || { label: s, color: 'bg-slate-100 text-slate-600' };
                          return <Badge key={s} className={`${meta.color} text-[10px] border`}>{meta.label}</Badge>;
                        })}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{c.transactions_count || 0}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtEur(c.total_invoiced)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtEur(c.last_amount)}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtDate(c.last_seen_date)}</td>
                    <td className="px-3 py-2 text-right">
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 hover:bg-blue-100"
                          onClick={(e) => { e.stopPropagation(); setEditingContact(c); }}
                          data-testid={`edit-contact-${c.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {editingContact && (
        <PaymentContactEditModal
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSaved={() => {
            fetchContacts();
          }}
        />
      )}

      <Dialog open={showDups} onOpenChange={setShowDups}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="iban-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-amber-600" />
              Duplicati IBAN — {dupGroups.length} gruppi da unire
            </DialogTitle>
          </DialogHeader>
          {dupGroups.length === 0 ? (
            <p className="text-sm text-emerald-700 py-4">✓ Nessun duplicato: ogni IBAN ha un solo contatto in rubrica.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Per ogni gruppo scegli il contatto da <strong>tenere</strong> (gli altri diventano suoi alias e vengono eliminati).
              </p>
              {dupGroups.map((g) => (
                <Card key={g.iban} className="p-3 border-amber-200 bg-amber-50/40" data-testid={`dup-group-${g.iban}`}>
                  <p className="font-mono text-xs text-slate-600 mb-2">IBAN: {g.iban}</p>
                  <div className="space-y-1">
                    {g.contacts.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name={`keep-${g.iban}`}
                          checked={dupKeep[g.iban] === c.id}
                          onChange={() => setDupKeep((prev) => ({ ...prev, [g.iban]: c.id }))}
                          data-testid={`dup-keep-radio-${c.id}`}
                        />
                        <span className="font-medium">{c.name}</span>
                        {(c.aliases || []).length > 0 && (
                          <span className="text-xs text-slate-500">alias: {(c.aliases || []).join(', ')}</span>
                        )}
                        {c.category && <Badge variant="outline" className="text-[10px]">{c.category}</Badge>}
                        {g.suggested_keep_id === c.id && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">consigliato</Badge>
                        )}
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      onClick={() => mergeGroup(g)}
                      disabled={merging === g.iban}
                      data-testid={`merge-group-btn-${g.iban}`}
                    >
                      {merging === g.iban
                        ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Unione...</>
                        : <><GitMerge className="h-4 w-4 mr-1" /> Unisci gruppo</>}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
